import { getCities } from '../../../api/authApi'
import { createCorporate, editCorporate, getCorporateDetail } from '../../../api/corporateApi'
import { getStudiosSuggestion, getStudiosSuggestionsByIds } from '../../../api/studiosApi'
import { getTagsByContext } from '../../../api/tagsApi'
import DebounceSelect from '../../../components/debounce-select/debounce-select'
import { MAX_QS_STRINGIFY_LENGTH } from '../../../helpers/constant'
import { getOptions } from '../../../helpers/utils'
import translate from '../../../i18n'
import { ICity } from '../../prelaunch-organization-forms-container/share-types'
import { TaggingContextEnums } from '../../tags-container/constant'
import { ITag } from '../../tags-container/share-type'
import { CorporateRequest, ICorporate } from '../share-type'
import styles from './corporate-form.module.less'
import { ExclamationCircleFilled, InfoCircleOutlined } from '@ant-design/icons'
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  notification,
  Space,
  Switch,
} from 'antd'
import Select from 'antd/es/select'
import TextArea from 'antd/lib/input/TextArea'
import dayjs from 'dayjs'
import * as _ from 'lodash'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

const { confirm } = Modal

const CorporateForm = () => {
  const [form] = Form.useForm()

  const params = useParams()
  const navigation = useNavigate()
  const [corporateId] = useState<number | undefined>(Number(params?.id))
  const [corporate, setCorporate] = useState<ICorporate>()
  const [defaultStudioSuggestionsOptions, setDefaultStudioSuggestionsOptions] = useState<
    { label: string; value: number }[]
  >([])
  const [defaultCitiesOptions, setDefaultCitiesOptions] = useState<
    { label: string; value: number }[]
  >([])
  const [activityTags, setActivityTags] = useState<ITag[]>([])

  useEffect(() => {
    if (corporateId) {
      getCorporateDetail(corporateId).then(data => {
        if (data.corporate.contractDeadline) {
          data.corporate.contractDeadline = dayjs(data.corporate.contractDeadline)
        }
        if (data.corporate.nextDistributionTime) {
          data.corporate.nextDistributionTime = dayjs(
            data.corporate.nextDistributionTime,
            'YYYY-MM-DDTHH:mm:ss',
          )
        }
        setCorporate(data.corporate)

        if (
          data.corporate.disallowStudioIds?.length &&
          data.corporate.disallowStudioIds.length <= MAX_QS_STRINGIFY_LENGTH
        ) {
          getStudiosSuggestionsByIds(data.corporate.disallowStudioIds).then(studioSuggestions => {
            const sortStudioSuggestions = _.sortBy(studioSuggestions, suggestion =>
              _.findIndex(data.corporate.disallowStudioIds, id => id === suggestion.id),
            )
            setDefaultStudioSuggestionsOptions(getOptions(sortStudioSuggestions))
          })
        }
        form.setFieldsValue({
          ...data.corporate,
          price: data.corporate.price === null ? undefined : data.corporate.price / 100,
        })
      })
    }

    getCities().then(res => {
      const data = res as any as Array<ICity>

      setDefaultCitiesOptions(
        getOptions(
          data.map(d => ({
            id: d.id,
            name: d.translations.find(t => t.locale === 'zh-CN')?.name || '',
          })),
        ),
      )
    })

    getTagsByContext(TaggingContextEnums.ACTIVITIES).then(res => {
      if (res) {
        setActivityTags(Object.values(res).flat())
      }
    })
  }, [corporateId])

  const getTagsOptions = (tags: ITag[]) => {
    return tags.map(tag => {
      const categoryAndSubCategoryLabel = [tag.category, tag.subCategory]
        .filter(item => item)
        .join('/')
      const tagName = tag.tagTranslations.find(t => t.locale === 'zh-CN')?.name
      return {
        label: `${tagName} (${categoryAndSubCategoryLabel})`,
        value: tag.id,
      }
    })
  }

  const onFinish = (values: CorporateRequest) => {
    const data = new FormData()
    if (values.file && values.file.length) {
      data.append('file', values.file[0].originFileObj)
    }

    data.append('name', values.name)
    if (values.price != null) {
      data.append('price', String(Math.round(values.price * 100)))
    }
    data.append('isActive', String(values.isActive))
    data.append('isTrial', String(values.isTrial))
    data.append('allowShowCreditsPool', String(values.allowShowCreditsPool))
    data.append(
      'unusedDistributedCreditsThreshold',
      String(values.unusedDistributedCreditsThreshold),
    )
    data.append('enableReclaimCancelBooking', String(values.enableReclaimCancelBooking))

    if (values.creditsValidityPeriodInDays) {
      data.append('creditsValidityPeriodInDays', String(values.creditsValidityPeriodInDays))
    }
    if (values.studioIds) {
      data.append('studioIds', JSON.stringify(values.studioIds))
    }
    if (values.disallowStudioIds) {
      data.append('disallowStudioIds', JSON.stringify(values.disallowStudioIds))
    }
    if (values.disallowCityList) {
      data.append('disallowCityList', JSON.stringify(values.disallowCityList))
    }
    if (values.disallowActivityList) {
      data.append('disallowActivityList', JSON.stringify(values.disallowActivityList))
    }
    if (values.maxCreditsUsageThreshold) {
      data.append('maxCreditsUsageThreshold', String(values.maxCreditsUsageThreshold))
    }

    data.append('enabledMixPayment', String(values.enabledMixPayment))
    if (values.principal) {
      data.append('principal', values.principal)
    }
    if (values.cooperationMode) {
      data.append('cooperationMode', values.cooperationMode)
    }
    if (values.contractDeadline) {
      data.append('contractDeadline', dayjs(values.contractDeadline).utc().format())
    }
    if (values.distributionFrequency) {
      data.append('distributionFrequency', values.distributionFrequency)
    }
    if (values.nextDistributionTime) {
      data.append('nextDistributionTime', dayjs(values.nextDistributionTime).utc().format())
    }
    if (values.distributionRuleRemark) {
      data.append('distributionRuleRemark', values.distributionRuleRemark)
    }

    if (corporateId) {
      if (corporate?.isActive && !values.isActive) {
        confirm({
          title: '是否冻结企业?',
          icon: <ExclamationCircleFilled />,
          content: '如果冻结企业，该企业下所有用户也会被冻结',
          onOk() {
            onConfirmUpdateCorporte(data)
          },
        })
      } else {
        onConfirmUpdateCorporte(data)
      }
    } else {
      createCorporate(data)
        .then(res => {
          notification.success({
            title: '创建合作企业成功',
          })
          navigation(`/corporates/${res.id}`)
        })
        .catch(err => {
          notification.error({
            title: '创建合作企业失败',
            description: err?.message || '网络请求失败，请稍后重试',
          })
        })
    }
  }

  const onConfirmUpdateCorporte = (data: FormData) => {
    if (corporateId) {
      editCorporate(corporateId, data)
        .then(res => {
          notification.success({
            title: '编辑合作企业成功',
          })
          navigation(`/corporates/${res.id}`)
        })
        .catch(err => {
          notification.error({
            title: '编辑合作企业失败',
            description: err?.message || '网络请求失败，请稍后重试',
          })
        })
    }
  }

  async function fetchStudioList(keyword: string): Promise<{ label: string; value: number }[]> {
    return getStudiosSuggestion(keyword).then(res => {
      return getOptions(res)
    })
  }

  async function fetchCityList(): Promise<{ label: string; value: number }[]> {
    return defaultCitiesOptions
  }

  // const normFile = (e: any) => {
  //   if (Array.isArray(e)) {
  //     return e
  //   }
  //   return e?.fileList
  // }
  //
  // const beforeUploadUserFile = () => {
  //   return false
  // }

  return (
    <Form
      name="corporate"
      labelCol={{ span: 6 }}
      wrapperCol={{ span: 12 }}
      initialValues={{
        isActive: false,
        unusedDistributedCreditsThreshold: 0,
        isTrial: false,
        enabledMixPayment: true,
      }}
      onFinish={onFinish}
      autoComplete="off"
      form={form}
      className={styles['corporate-form']}
    >
      <Form.Item label="名称" name="name" rules={[{ required: true, message: '请输入名称！' }]}>
        <Input />
      </Form.Item>
      <Form.Item label="是否激活" name="isActive" valuePropName="checked">
        <Switch />
      </Form.Item>
      <Form.Item label="是否是试用企业" name="isTrial" valuePropName="checked">
        <Switch />
      </Form.Item>
      <Form.Item
        label="是否允许对用户显示点数池"
        name="allowShowCreditsPool"
        valuePropName="checked"
      >
        <Switch />
      </Form.Item>
      <Form.Item name="disallowActivityList" label="不允许预约的activities">
        <Select
          mode="multiple"
          allowClear
          placeholder="搜索activities"
          options={getTagsOptions(activityTags)}
        />
      </Form.Item>
      <Form.Item label="单次预约允许使用最大点数">
        <Space.Compact>
          <Form.Item name="maxCreditsUsageThreshold" noStyle>
            <InputNumber />
          </Form.Item>
          <div className="addon-after">点</div>
        </Space.Compact>
      </Form.Item>
      <Form.Item
        label="是否允许超过单次最大使用点数后使用混合支付"
        name="enabledMixPayment"
        valuePropName="checked"
      >
        <Switch />
      </Form.Item>
      {/* <Form.Item */}
      {/*   name="file" */}
      {/*   label={translate('corporate.studioIds.bulkUpload')} */}
      {/*   valuePropName="fileList" */}
      {/*   getValueFromEvent={normFile} */}
      {/* > */}
      {/*   <Upload accept=".csv" beforeUpload={beforeUploadUserFile} maxCount={1}> */}
      {/*     <Button icon={<UploadOutlined />}>Upload</Button> */}
      {/*   </Upload> */}
      {/* </Form.Item> */}
      <Form.Item label="搜索不允许预约场馆" name="disallowStudioIds">
        <DebounceSelect
          mode="multiple"
          style={{ minWidth: 130 }}
          showSearch={true}
          allowClear
          placeholder="搜索场馆"
          fetchOptions={fetchStudioList}
          defaultOptions={defaultStudioSuggestionsOptions}
        />
      </Form.Item>

      <Form.Item label="不允许预约的城市" name="disallowCityList">
        <DebounceSelect
          mode="multiple"
          style={{ minWidth: 130 }}
          showSearch={true}
          allowClear
          placeholder="搜索城市"
          fetchOptions={fetchCityList}
          defaultOptions={defaultCitiesOptions}
        />
      </Form.Item>

      <Form.Item
        label={translate('corporate.unusedDistributedCreditsThreshold')}
        tooltip={{
          title: translate('corporate.unusedDistributedCreditsThreshold.tips'),
          icon: <InfoCircleOutlined />,
        }}
        rules={[
          {
            required: true,
            message: translate('corporate.unusedDistributedCreditsThreshold.required'),
          },
        ]}
      >
        <Space.Compact>
          <Form.Item name="unusedDistributedCreditsThreshold" noStyle>
            <InputNumber min={0} />
          </Form.Item>
          <div className="addon-after">点</div>
        </Space.Compact>
      </Form.Item>
      <Form.Item
        label={translate('corporate.enableReclaimCancelBooking')}
        name="enableReclaimCancelBooking"
        tooltip={{
          title: translate('corporate.enableReclaimCancelBooking.tips'),
          icon: <InfoCircleOutlined />,
        }}
        rules={[
          {
            required: true,
          },
        ]}
        valuePropName="checked"
      >
        <Switch />
      </Form.Item>
      <Form.Item label={translate('corporate.principal')} name="principal">
        <Input maxLength={100} />
      </Form.Item>
      <Form.Item label={translate('corporate.cooperationMode')} name="cooperationMode">
        <Input maxLength={100} />
      </Form.Item>
      <Form.Item label={translate('corporate.contractDeadline')} name="contractDeadline">
        <DatePicker />
      </Form.Item>
      <Form.Item label={translate('corporate.distributionFrequency')} name="distributionFrequency">
        <Input maxLength={100} />
      </Form.Item>
      <Form.Item label={translate('corporate.nextDistributionTime')} name="nextDistributionTime">
        <DatePicker showTime />
      </Form.Item>
      <Form.Item
        label={translate('corporate.distributionRuleRemark')}
        name="distributionRuleRemark"
      >
        <TextArea maxLength={500} showCount={true} />
      </Form.Item>
      <Form.Item label={translate('corporate.price')}>
        <Space.Compact>
          <Form.Item name="price" noStyle>
            <InputNumber min={0} precision={2} />
          </Form.Item>
          <div className="addon-after">元/点</div>
        </Space.Compact>
      </Form.Item>
      <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
        <Button type="primary" htmlType="submit">
          提交
        </Button>
      </Form.Item>
    </Form>
  )
}

export default CorporateForm
